import { useParams } from "@tanstack/react-router";
import { MessageSquare } from "lucide-react";
import type { ModuleManifest } from "@praesto/contracts";
import { useCore } from "../lib/core";

/**
 * Global floating entry point to the AI assistant — reachable from every route.
 * Opens the assistant as a full-screen overlay ({@link AssistantOverlay}) so
 * the user never loses their place: closing returns to the exact screen.
 *
 * Today the overlay shows the procedure module's assistant (SOP Q&A); it is
 * expected to grow into an app-wide helper. Host stays free of a static
 * @praesto/procedure import — the overlay pulls the component from the manifest.
 *
 * Visible only when the procedure module is active for the tenant AND the user
 * may query the assistant (`query:sop-assistant`). Parked at `.bottom-nav-offset`
 * (a clear gap above the bottom nav + safe area); module FABs stack above it via
 * `.bottom-nav-offset-2`.
 */
export function AssistantFab({
  manifests,
  onOpen,
}: {
  manifests: ReadonlyArray<ModuleManifest>;
  onOpen: () => void;
}) {
  const core = useCore();
  const { tenantSlug } = useParams({ strict: false }) as { tenantSlug?: string };

  const procedureActive = manifests.some((m) => m.id === "procedure");
  const canQuery = core.rbac.can("query", "sop-assistant");

  if (!tenantSlug || !procedureActive || !canQuery) return null;

  const label = core.i18n.t("shell.assistant.fab");

  // The Spectrum diagonal is the assistant's signature surface (C3F v18) and
  // this FAB is one of the few places allowed to wear it. MessageSquare, not
  // Sparkles: the brand bans decorative AI symbols — Sparkles stays only in
  // the sub-nav entry, where the manifest names the destination.
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={label}
      title={label}
      className="fixed bottom-nav-offset right-4 z-30 flex h-14 w-14 items-center justify-center rounded-full ai-mark text-praesto-paper shadow-spectrum transition-transform hover:brightness-110 active:scale-95"
    >
      <MessageSquare size={24} aria-hidden />
    </button>
  );
}
